import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, EMPTY } from 'rxjs';
import { map, shareReplay, switchMap, expand, reduce } from 'rxjs/operators';
import { APP_CONFIG } from '@environments/environment';
import { ApiService } from './api.service';
import { OrganizationResponseDto, CreateOrganizationRequestDto, UpdateOrganizationRequestDto } from '@qc/shared';
import { ResponsePaginatedWrapper } from '@/app/shared/dto';

interface PaginatedResult<T> {
    items: T[];
    meta: {
        totalCount: number;
        totalPages: number;
        currentPage: number;
        limit: number;
    };
}

type Organization = OrganizationResponseDto;

@Injectable({
    providedIn: 'root',
})
export class OrganizationService extends ApiService {
    protected get basePath(): string {
        return '/organizations';
    }

    // Cache for individual organizations
    private organizationsCache = new Map<string, Observable<Organization>>();

    private fetchAllPages<T>(url: string): Observable<T[]> {
        const getPage = (pageNumber: number): Observable<ResponsePaginatedWrapper<T>> => {
            const params = new HttpParams().set('page', pageNumber.toString()).set('limit', '100');
            return this.http.get<ResponsePaginatedWrapper<T>>(url, { params });
        };

        return getPage(1).pipe(
            expand((response) => {
                if (response.meta.currentPage < response.meta.totalPages) {
                    return getPage(response.meta.currentPage + 1);
                }
                return EMPTY;
            }),
            map((response) => response.data),
            reduce((acc, data) => acc.concat(data), [] as T[]),
        );
    }

    getAllOrganizations(): Observable<Organization[]> {
        return this.fetchAllPages<Organization>(this.organizationsApiUrl);
    }

    searchOrganizations(
        searchTerm: string = '',
        page: number = 1,
        limit: number = 50,
    ): Observable<PaginatedResult<Organization>> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString())
            .set('search', searchTerm);

        return this.http.get<ResponsePaginatedWrapper<Organization>>(this.organizationsApiUrl, { params }).pipe(
            map((response) => ({
                items: response.data,
                meta: response.meta,
            })),
        );
    }

    getOrganization(id: string): Observable<Organization> {
        // Check if already cached
        if (this.organizationsCache.has(id)) {
            return this.organizationsCache.get(id)!;
        }

        const request$ = this.http.get<any>(`${this.organizationsApiUrl}/${id}`).pipe(
            map((response) => response.data || response),
            shareReplay(1),
        );

        // Cache the observable
        this.organizationsCache.set(id, request$);
        return request$;
    }

    getOrganizationsBatch(ids: string[]): Observable<Organization[]> {
        if (!Array.isArray(ids) || ids.length === 0) {
            return of([]);
        }

        return this.http
            .post<any>(`${this.organizationsApiUrl}/batch`, { ids })
            .pipe(map((response) => response.data || response || []));
    }

    createOrganization(organizationData: CreateOrganizationRequestDto): Observable<Organization> {
        return this.http.post<any>(this.organizationsApiUrl, organizationData).pipe(map((response) => response.data));
    }

    updateOrganization(id: string, organizationData: UpdateOrganizationRequestDto): Observable<Organization> {
        return this.http.patch<any>(`${this.organizationsApiUrl}/${id}`, organizationData).pipe(
            map((response) => response.data),
            switchMap((updatedOrganization) => {
                // Clear cache for this organization after update
                this.clearOrganizationCache(id);
                return of(updatedOrganization);
            }),
        );
    }

    deleteOrganization(id: string): Observable<void> {
        return this.http.delete<void>(`${this.organizationsApiUrl}/${id}`).pipe(
            switchMap((result) => {
                // Clear cache for this organization after deletion
                this.clearOrganizationCache(id);
                return of(result);
            }),
        );
    }

    clearOrganizationCache(id: string): void {
        this.organizationsCache.delete(id);
    }

    clearAllOrganizationsCache(): void {
        this.organizationsCache.clear();
    }

    private get organizationsApiUrl(): string {
        return `${this.apiUrl}/organizations`;
    }
}
